import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiLoader,
  FiSend,
  FiX,
} from 'react-icons/fi';
import PackagePassengerForm, {
  emptyPassenger,
} from '../PackagePassengerForm/PackagePassengerForm';
import { enquirePackageApi } from '../../api/packagesApi';
import { PACKAGE_LIMITS } from '../../constants/packageConstants';
import {
  buildPackageEnquiryRequest,
  validatePassengers,
} from '../../utils/packageUtils';
import '../../styles/packagesShared.scss';
import './PackageEnquiryModal.scss';

/**
 * "Send an enquiry": instead of paying now, the agent sends the supplier the
 * lead traveller and a message, and the supplier follows up offline. Nothing
 * is charged. Always available — nothing has to be picked first.
 */
function PackageEnquiryModal({
  show,
  onHide,
  packageKey,
  searchId,
  packageName,
}) {
  const [lead, setLead] = useState(() => emptyPassenger('ADULT'));
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState(null); // { message, reference }
  const [sent, setSent] = useState(false);

  // a fresh form every time it opens
  useEffect(() => {
    if (!show) return;
    setLead(emptyPassenger('ADULT'));
    setMessage('');
    setErrors({});
    setFailure(null);
    setSent(false);
  }, [show]);

  const updateLead = (index, patch) => {
    setLead((p) => ({ ...p, ...patch }));
    setErrors({});
  };

  const submit = async () => {
    if (sending) return;
    const next = validatePassengers([lead]);
    if (!message.trim()) next.message = 'Tell the supplier what you need';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSending(true);
    setFailure(null);
    const read = await enquirePackageApi(
      buildPackageEnquiryRequest({ packageKey, searchId, lead, message }),
    );
    setSending(false);
    if (read.ok) {
      setSent(true);
      return;
    }
    setFailure({
      message: read.network
        ? 'We could not reach the server. Check your connection and try again.'
        : read.message || 'The enquiry could not be sent. Please try again.',
      reference: read.correlationId,
    });
  };

  return (
    <Modal
      show={show}
      onHide={sending ? undefined : onHide}
      size="lg"
      centered
      backdrop={sending ? 'static' : true}
      keyboard={!sending}
      dialogClassName="pkg-modal pkg-enquiry"
    >
      <Modal.Body>
        {!sending && (
          <button
            type="button"
            className="pkg-modal__close"
            onClick={onHide}
            aria-label="Close"
          >
            <FiX />
          </button>
        )}

        {sent ? (
          <div className="pkg-enquiry__done">
            <FiCheckCircle />
            <h3>Enquiry sent</h3>
            <p>
              The supplier will contact {lead.firstName.trim() || 'you'} on{' '}
              <strong>{lead.email}</strong> about{' '}
              {packageName || 'this package'}. Nothing has been charged.
            </p>
            <button
              type="button"
              className="pkg-state__primary"
              onClick={onHide}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <h3 className="pkg-modal__title">Send an enquiry</h3>
            <p className="pkg-modal__sub">
              Ask the supplier about dates, prices or anything else for{' '}
              <strong>{packageName}</strong>. Nothing is charged.
            </p>

            <PackagePassengerForm
              index={0}
              passenger={lead}
              onChange={updateLead}
              errors={errors}
              lead
            />

            <label className="pkg-enquiry__message" htmlFor="pkg-enquiry-msg">
              <span className="required">Message</span>
              <textarea
                id="pkg-enquiry-msg"
                rows={4}
                maxLength={PACKAGE_LIMITS.MAX_REQUESTS}
                placeholder="e.g. Interested — please share November dates."
                value={message}
                onChange={(e) => {
                  setMessage(
                    e.target.value.slice(0, PACKAGE_LIMITS.MAX_REQUESTS),
                  );
                  setErrors((x) => {
                    const { message: drop, ...rest } = x;
                    return rest;
                  });
                }}
              />
              <small>
                {message.length}/{PACKAGE_LIMITS.MAX_REQUESTS}
              </small>
            </label>
            {errors.message && (
              <span className="pkg-field-error">{errors.message}</span>
            )}

            {failure && (
              <div className="pkg-alert mt-3" role="alert">
                <FiAlertCircle />
                <span>
                  {failure.message}
                  {failure.reference && (
                    <small className="d-block">Ref {failure.reference}</small>
                  )}
                </span>
              </div>
            )}

            <div className="pkg-enquiry__actions">
              <button
                type="button"
                className="pkg-state__secondary"
                onClick={onHide}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pkg-state__primary"
                onClick={submit}
                disabled={sending}
              >
                {sending ? (
                  <>
                    <FiLoader className="pkg-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <FiSend /> Send enquiry
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

PackageEnquiryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  packageKey: PropTypes.string.isRequired,
  searchId: PropTypes.string,
  packageName: PropTypes.string,
};

PackageEnquiryModal.defaultProps = {
  searchId: '',
  packageName: '',
};

export default PackageEnquiryModal;
